import React, { useState, useEffect } from 'react';
import "./GerenciarFornecedor.css";
import { Button, message, Modal, Table, Space, DatePicker } from 'antd';
import { EditOutlined, EyeOutlined, PoweroffOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import dayjs from 'dayjs';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { TextField } from '@mui/material';
import api from '../../../../services/api';
import API_CONFIG from '../../../../config/api.config';

const GerenciarFornecedor = () => {
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const navigate = useNavigate();
  const [fornecedores, setFornecedores] = useState([]);
  const [fornecedoresOriginais, setFornecedoresOriginais] = useState([]);
  const [modalDetalhesVisivel, setModalDetalhesVisivel] = useState(false);
  const [fornecedorSelecionado, setFornecedorSelecionado] = useState(null);
  const [fornecedorSelecionadoFiltro, setFornecedorSelecionadoFiltro] = useState(null);
  const [loading, setLoading] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 600 });

  const [fornecedoresParaSelect, setFornecedoresParaSelect] = useState([
    {
      value: null,
      label: 'Selecione o Fornecedor'
    }
  ]);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 8,
    total: 0
  });

  useEffect(() => {
    carregarFornecedores();
  }, []);

  const carregarFornecedores = async (page = 1, params = {}) => {
    try {
      setLoading(true);
      const queryParams = {
        page,
        ...params
      };

      const response = await api.get(API_CONFIG.ENDPOINTS.FORNECEDOR.CONSULTAR_FOR, { params: queryParams });
      
      if (response.data.status) {
        const fornecedoresData = response.data.fornecedores.data || [];
        const paginationData = response.data.fornecedores;

        const fornecedoresFormatados = fornecedoresData.map(forn => ({
          id: forn.FOR_ID,
          nome: forn.FOR_RAZAO_SOCIAL,
          dataAbertura: forn.FOR_DATA_VINCULACAO,
          status: forn.FOR_STATUS === 'ATIVO' ? 'Ativo' : 'Inativo',
          endereco: `${forn.endereco?.END_LOGRADOURO || ''}, ${forn.endereco?.END_CIDADE || ''} - ${forn.endereco?.END_ESTADO || ''}`,
          telefone: forn.FOR_TELEFONE,
          telefoneBkp: forn.FOR_TELEFONE_BKP,
          cnpj: forn.FOR_CNPJ,
          enderecoCompleto: {
            cep: forn.endereco?.END_CEP,
            pais: forn.endereco?.END_PAIS,
            estado: forn.endereco?.END_ESTADO,
            cidade: forn.endereco?.END_CIDADE,
            logradouro: forn.endereco?.END_LOGRADOURO
          }
        }));

        setFornecedores(fornecedoresFormatados);
        setFornecedoresOriginais(fornecedoresFormatados);
        setPagination({
          current: paginationData.current_page,
          pageSize: paginationData.per_page,
          total: paginationData.total
        });

        const fornecedoresSelect = [
          {
            value: null,
            label: 'Selecione o Fornecedor'
          },
          ...fornecedoresData.map(forn => ({
            value: forn.FOR_ID,
            label: forn.FOR_RAZAO_SOCIAL
          }))
        ];
        setFornecedoresParaSelect(fornecedoresSelect);
      } else {
        message.error('Erro ao carregar fornecedores');
      }
    } catch (error) {
      //console.error('Erro ao carregar fornecedores:', error);
      message.error('Erro ao carregar fornecedores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleMenuChange = (event) => {
      setIsMenuExpanded(!event.detail.isCollapsed);
    };
    window.addEventListener('menuStateChange', handleMenuChange);
    return () => window.removeEventListener('menuStateChange', handleMenuChange);
  }, []);

  const handleFiltrarFornecedor = (idSelecionado) => {
    setFornecedorSelecionadoFiltro(idSelecionado);
    if (!idSelecionado) {
      setFornecedores(fornecedoresOriginais);
    } else {
      setFornecedores(fornecedoresOriginais.filter(f => f.id === idSelecionado));
    }
  };

  const handleVisualizar = async (fornecedor) => {
    try {
      const url = `/api/fornecedores/${fornecedor.id}`;
      const response = await api.get(url);
      
      if (response.data.status) {
        const fornecedorDetalhado = response.data.fornecedor;
        
        if (!fornecedorDetalhado) {
          message.error('Fornecedor não encontrado');
          return;
        }

        setFornecedorSelecionado({
          id: fornecedorDetalhado.FOR_ID,
          nome: fornecedorDetalhado.FOR_RAZAO_SOCIAL,
          dataAbertura: fornecedorDetalhado.FOR_DATA_VINCULACAO,
          status: fornecedorDetalhado.FOR_STATUS === 'ATIVO' ? 'Ativo' : 'Inativo',
          endereco: `${fornecedorDetalhado.endereco?.END_LOGRADOURO || ''}, ${fornecedorDetalhado.endereco?.END_CIDADE || ''} - ${fornecedorDetalhado.endereco?.END_ESTADO || ''}`,
          telefone: fornecedorDetalhado.FOR_TELEFONE,
          telefoneBkp: fornecedorDetalhado.FOR_TELEFONE_BKP,
          cnpj: fornecedorDetalhado.FOR_CNPJ,
          enderecoCompleto: {
            cep: fornecedorDetalhado.endereco?.END_CEP,
            pais: fornecedorDetalhado.endereco?.END_PAIS,
            estado: fornecedorDetalhado.endereco?.END_ESTADO,
            cidade: fornecedorDetalhado.endereco?.END_CIDADE,
            logradouro: fornecedorDetalhado.endereco?.END_LOGRADOURO
          }
        });
        setModalDetalhesVisivel(true);
      } else {
        message.error('Erro ao carregar detalhes do fornecedor');
      }
    } catch (error) {
      //console.error('Erro ao carregar detalhes do fornecedor:', error);
      message.error('Erro ao carregar detalhes do fornecedor');
    }
  };

  const handleEditar = (fornecedor) => {
    navigate(`/adm/editar-fornecedor/${fornecedor.id}`, { state: { fornecedor } });
  };

  const handleStatus = async (fornecedor) => {
    try {
      const novoStatus = fornecedor.status === 'Ativo' ? 'INATIVO' : 'ATIVO';
      
      const response = await api.put(`/api/fornecedores/${fornecedor.id}`, {
        FOR_STATUS: novoStatus
      });

      if (response.data.status) {
        message.success(`Status do fornecedor alterado para ${novoStatus === 'ATIVO' ? 'Ativo' : 'Inativo'}`);
        carregarFornecedores(); // Recarrega a lista
      } else {
        message.error('Erro ao alterar status do fornecedor');
      }
    } catch (error) {
      //console.error('Erro ao alterar status do fornecedor:', error);
      message.error('Erro ao alterar status do fornecedor');
    }
  };

  const handleVoltar = () => {
    navigate('/adm');
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80
    },
    {
      title: 'Nome',
      dataIndex: 'nome',
      key: 'nome',
    },
    {
      title: 'Data de Abertura',
      dataIndex: 'dataAbertura',
      key: 'dataAbertura',
      width: 180,
      render: (data) => dayjs(data).format('DD/MM/YYYY')
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <span style={{
          color: status === 'Ativo' ? '#008026' : '#ff4d4f',
          fontWeight: 500
        }}>
          {status}
        </span>
      )
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => handleVisualizar(record)}
            className="visualizar-btn"
          >
            Visualizar
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEditar(record)}
          >
            Editar
          </Button>
          <Button
            icon={<PoweroffOutlined />}
            onClick={() => handleStatus(record)}
            danger={record.status === 'Ativo'}
          >
            {record.status === 'Ativo' ? 'Inativar' : 'Ativar'}
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div className={`home-container ${isMenuExpanded ? 'menu-expanded' : ''}`}>
      <div className="modal-wrapper">
        <h1 className="modal-title">Gerenciar Fornecedores</h1>
        <div className="modal-content">
          <div className="search-container" style={{ justifyContent: 'center', alignItems: 'center', display: 'flex', flexWrap: 'wrap' }}>
            <FormControl
              fullWidth
              size="small"
              variant="outlined"
              style={{ marginBottom: 8 }}
            >
              <InputLabel id="select-fornecedor-label">Selecione o Fornecedor</InputLabel>
              <Select
                labelId="select-fornecedor-label"
                value={fornecedorSelecionadoFiltro}
                label="Selecione o Fornecedor"
                onChange={(e) => handleFiltrarFornecedor(e.target.value)}
                disabled={loading}
              >
                {fornecedoresParaSelect.map(forn => (
                  <MenuItem key={forn.value || 'null'} value={forn.value}>
                    {forn.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          {/* Tabela responsiva */}
          {isMobile ? (
            <div className="tabela-mobile">
              {fornecedores.map((item) => (
                <div className="tabela-mobile-item" key={item.id}>
                  <div className="item-nome">{item.nome}</div>
                  <div className="item-quantidade">Status: {item.status}</div>
                  <div className="item-descricao">Endereço: {item.endereco}</div>
                  <div className="item-botoes">
                    <Button size="small" onClick={() => handleVisualizar(item)} className="visualizar-btn" loading={loading}>Visualizar</Button>
                    <Button size="small" onClick={() => handleEditar(item)} disabled={loading}>Editar</Button>
                    <Button size="small" danger={item.status === 'Ativo'} onClick={() => handleStatus(item)} loading={loading}>
                      {item.status === 'Ativo' ? 'Inativar' : 'Ativar'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={fornecedores}
              rowKey="id"
              pagination={pagination}
              className="tabela-solicitacao"
              loading={loading}
            />
          )}
        </div>

        <Modal
          title="Detalhes do Fornecedor"
          open={modalDetalhesVisivel}
          onCancel={() => {
            setModalDetalhesVisivel(false);
            setFornecedorSelecionado(null);
          }}
          footer={[
            <Button 
              key="fechar" 
              onClick={() => {
                setModalDetalhesVisivel(false);
                setFornecedorSelecionado(null);
              }}
            >
              Fechar
            </Button>
          ]}
          width={800}
          styles={{
            body: {
              maxHeight: '70vh',
              overflowY: 'auto',
              padding: '24px'
            }
          }}
          destroyOnClose={true}
        >
          {fornecedorSelecionado && (
            <div className="detalhe-expandido-item" style={{ margin: 0, flex: 1, overflowY: 'auto' }}>
              <div className="col-esq">
                <div>
                  <span className="label-icone">Nome:</span>
                  <div className="valor-destaque">{fornecedorSelecionado.nome}</div>
                </div>
                <div>
                  <span className="label-icone">CNPJ:</span>
                  <div className="valor-destaque">{fornecedorSelecionado.cnpj}</div>
                </div>
                <div>
                  <span className="label-icone">Telefone:</span>
                  <div className="valor-destaque">{fornecedorSelecionado.telefone}</div>
                </div>
                {fornecedorSelecionado.telefoneBkp && (
                  <div>
                    <span className="label-icone">Telefone Backup:</span>
                    <div className="valor-destaque">{fornecedorSelecionado.telefoneBkp}</div>
                  </div>
                )}
              </div>
              <div className="col-dir">
                <div>
                  <span className="label-icone">Endereço:</span>
                  <div className="valor-destaque">{fornecedorSelecionado.endereco}</div>
                </div>
                <div>
                  <span className="label-icone">CEP:</span>
                  <div className="valor-destaque">{fornecedorSelecionado.enderecoCompleto.cep}</div>
                </div>
                <div>
                  <span className="label-icone">Cidade:</span>
                  <div className="valor-destaque">{fornecedorSelecionado.enderecoCompleto.cidade}</div>
                </div>
                <div>
                  <span className="label-icone">Estado:</span>
                  <div className="valor-destaque">{fornecedorSelecionado.enderecoCompleto.estado}</div>
                </div>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default GerenciarFornecedor;